import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Pencil, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useEditGate } from "@/components/EditGate";
import ComponentSelect from "@/components/ComponentSelect";
import { TagPicker } from "@/components/tags/TagPicker";
import { TagBadges } from "@/components/tags/TagBadges";
import { fetchFresh } from "@/lib/cache";

const SOURCE_LEVELS = { "L1.5": ["L0", "L1", "L1.5"], L2: ["L0", "L1", "L1.5"], L3: ["L2"] };
// Levels the user is allowed to newly ADD in the builder. L0 kept above so existing recipe rows still resolve.
const SELECTABLE_LEVELS = new Set(["L1", "L1.5", "L2"]);

// UI-only identity keeps each row stable when other rows are added or removed.
const componentRow = (component = {}) => ({ ref_type: "", ref_id: "", qty: "", ...component, rowId: crypto.randomUUID() });

function heatColor(pct, maxPct) {
  if (pct <= 0 || maxPct <= 0) return "transparent";
  const norm = Math.min(pct / maxPct, 1);
  const hue = 120 * (1 - norm);
  return `hsla(${hue}, 75%, 45%, 0.14)`;
}

export default function RecipeBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { requireEdit } = useEditGate();
  const isNew = !id;

  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(isNew);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [tags, setTags] = useState([]);
  const [level, setLevel] = useState("");
  const [levelError, setLevelError] = useState(false);
  const levelRef = useRef(null);
  const componentTriggers = useRef(new Map());
  const pendingComponentFocus = useRef(null);
  const [postWeight, setPostWeight] = useState("");
  const [outputUnit, setOutputUnit] = useState("g");
  const [portionQty, setPortionQty] = useState("");
  const [comps, setComps] = useState([]);

  const load = useCallback(async () => {
    try {
      const [ing, rec] = await Promise.all([api.get("/ingredients"), api.get("/recipes")]);
      setIngredients(ing.data);
      setRecipes(rec.data);
      if (id) {
        const r = rec.data.find((x) => x.id === id);
        if (!r) { toast.error("Recipe not found"); navigate("/recipes"); return; }
        setName(r.name); setLevel(r.level); setSku(r.sku || ""); setPostWeight(r.output_qty);
        setTags(r.tags || []);
        setOutputUnit(r.output_unit); setPortionQty(r.portion_qty || "");
        setComps(r.components.map((c) => componentRow({ ref_type: c.ref_type, ref_id: c.ref_id, qty: c.qty })));
      }
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const trigger = componentTriggers.current.get(pendingComponentFocus.current);
    if (trigger) {
      trigger.focus();
      pendingComponentFocus.current = null;
    }
  }, [comps]);

  useEffect(() => {
    if (!dirty || !editing) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty, editing]);

  const mark = (setter) => (v) => { setter(v); setDirty(true); };

  const options = useMemo(() => {
    const allowed = SOURCE_LEVELS[level] || [];
    const opts = [];
    if (allowed.includes("L0")) ingredients.forEach((i) => opts.push({ value: `ingredient:${i.id}`, label: i.name, level: "L0", unit: i.base_unit, unit_cost: i.cost_per_base_unit }));
    recipes.forEach((r) => {
      if (allowed.includes(r.level) && r.id !== id) opts.push({ value: `recipe:${r.id}`, label: r.name, level: r.level, unit: r.output_unit, unit_cost: r.unit_cost });
    });
    return opts.sort((a, b) => a.level.localeCompare(b.level) || a.label.localeCompare(b.label));
  }, [level, ingredients, recipes, id]);

  const optMap = useMemo(() => Object.fromEntries(options.map((o) => [o.value, o])), [options]);
  const selectableOptions = useMemo(() => options.filter((o) => SELECTABLE_LEVELS.has(o.level)), [options]);

  const lines = comps.map((c, idx) => {
    const key = `${c.ref_type}:${c.ref_id}`;
    const opt = optMap[key];
    const qty = Number(c.qty) || 0;
    return { idx, rowId: c.rowId, key, opt, ref_id: c.ref_id, rawQty: c.qty, qty, cost: qty * (opt?.unit_cost || 0) };
  });
  const preCookCost = lines.reduce((s, l) => s + l.cost, 0);
  const preCookWeight = lines.reduce((s, l) => s + l.qty, 0);
  const out = Number(postWeight) || 0;
  const yieldPct = preCookWeight > 0 && out > 0 ? (out / preCookWeight) * 100 : 0;
  const epUnitCost = out > 0 ? preCookCost / out : 0;
  const portionCost = epUnitCost * (Number(portionQty) || 0);
  const maxPct = preCookCost > 0 ? Math.max(...lines.map((l) => (l.cost / preCookCost) * 100), 0) : 0;

  const addComponent = () => {
    const row = componentRow();
    pendingComponentFocus.current = row.rowId;
    setComps((previous) => [...previous, row]);
    setDirty(true);
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (!e.repeat) addComponent();
  };

  const save = async (thenNavigate) => {
    if (!level) {
      setLevelError(true);
      levelRef.current?.focus();
      return;
    }
    if (!name.trim()) return toast.error("Name is required");
    if (out <= 0) return toast.error("Post-cooking weight must be > 0");
    const validComps = comps.filter((c) => c.ref_id).map((c) => ({ ref_type: c.ref_type, ref_id: c.ref_id, qty: Number(c.qty) || 0 }));
    if (validComps.length === 0) return toast.error("Add at least one component");
    requireEdit(async (password) => {
      setBusy(true);
      try {
        const body = { name: name.trim(), sku: sku.trim(), level, output_qty: out, output_unit: outputUnit, portion_qty: Number(portionQty) || 0, components: validComps, tags, password,
          wastage_pct: recipes.find(r => r.id === id)?.wastage_pct || 0 };
        let savedId = id;
        if (id) await api.put(`/recipes/${id}`, body);
        else { const { data } = await api.post("/recipes", body); savedId = data.id; }
        setDirty(false);
        toast.success("Recipe saved");
        fetchFresh("recipes").catch(() => {});
        if (thenNavigate) navigate(thenNavigate);
        else if (!id) navigate(`/recipes/${savedId}`);
        else { setEditing(false); load(); }
      } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    }, true);
  };

  const goBack = () => {
    if (editing && dirty) {
      if (window.confirm("You have unsaved changes. Save before leaving? (OK = save & leave, Cancel = stay)")) {
        save("/recipes");
      }
      return;
    }
    navigate("/recipes");
  };

  const handleComponentPaste = (e) => {
    const text = e.clipboardData.getData("text/plain");
    if (!text || !text.includes("\t")) return;
    e.preventDefault();
    const rows = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
    const added = [];
    const misses = [];
    rows.forEach((line) => {
      const [n, q] = line.split("\t");
      const opt = selectableOptions.find((o) => o.label.toLowerCase() === (n || "").trim().toLowerCase());
      if (opt) {
        const [t, rid] = opt.value.split(":");
        added.push(componentRow({ ref_type: t, ref_id: rid, qty: parseFloat(String(q).replace(/[^\d.]/g, "")) || 0 }));
      } else misses.push(n);
    });
    if (added.length) { setComps((p) => [...p, ...added]); setDirty(true); toast.success(`Pasted ${added.length} component(s)`); }
    if (misses.length) toast.error(`Not found: ${misses.join(", ")}`);
  };

  if (loading) return <div data-testid="builder-loading" className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 pb-24 md:p-8 md:pb-24" data-testid="recipe-builder-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button data-testid="builder-back-button" onClick={goBack} className="rounded-sm border border-zinc-300 bg-white p-2 text-zinc-600 transition-colors hover:bg-zinc-50 no-print">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <div className="min-w-0 [overflow-wrap:anywhere]">
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{isNew ? "New Recipe" : name}</h1>
            <p className="mt-0.5 text-sm text-zinc-500">{editing ? "Recipe builder — costs recalculate live" : "Recipe detail (read-only)"}</p>
          </div>
        </div>
        {!editing && (
          <button data-testid="builder-edit-button" onClick={() => requireEdit(() => setEditing(true))}
            className="inline-flex items-center gap-1.5 rounded-sm bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 no-print">
            <Pencil className="h-4 w-4" strokeWidth={1.5} /> Edit recipe
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6 max-w-4xl">
        <div className="col-span-2">
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Recipe Name</label>
          <input data-testid="builder-name-input" disabled={!editing} value={name} onChange={(e) => mark(setName)(e.target.value)} placeholder="e.g., Buttermilk Marinade"
            className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:bg-zinc-50 disabled:text-zinc-600" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">SKU</label>
          <input data-testid="builder-sku-input" disabled={!editing} value={sku} onChange={(e) => mark(setSku)(e.target.value)} placeholder="auto"
            className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:bg-zinc-50 disabled:text-zinc-600" />
        </div>
        <div>
          <label htmlFor="builder-level" className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Tier <span aria-hidden="true" className="text-rose-600">*</span></label>
          <select id="builder-level" ref={levelRef} data-testid="builder-level-select" required aria-required="true" aria-invalid={levelError} aria-describedby={levelError ? "builder-level-error" : undefined}
            disabled={!editing} value={level} onChange={(e) => { mark(setLevel)(e.target.value); setLevelError(false); }}
            className="mt-1 block w-full rounded-sm border border-zinc-300 bg-white px-2 py-2 text-sm focus:border-blue-600 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-600">
            <option value="" disabled>Select tier…</option>
            {["L1.5", "L2", "L3"].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          {levelError && <p id="builder-level-error" data-testid="builder-level-error" role="alert" className="mt-1 text-xs text-rose-600">Tier is required.</p>}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Post-Cook Weight</label>
          <input data-testid="builder-postweight-input" disabled={!editing} type="number" step="any" value={postWeight} onChange={(e) => mark(setPostWeight)(e.target.value)}
            className="mt-1 block w-full rounded-sm border border-zinc-300 px-3 py-2 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:bg-zinc-50 disabled:text-zinc-600" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Unit</label>
          <select data-testid="builder-unit-select" disabled={!editing} value={outputUnit} onChange={(e) => mark(setOutputUnit)(e.target.value)}
            className="mt-1 block w-full rounded-sm border border-zinc-300 bg-white px-2 py-2 text-sm focus:border-blue-600 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-600">
            {["g", "ml", "pc", "portion", "kg", "l"].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 max-w-4xl space-y-1.5">
        <div className="text-xs font-semibold uppercase text-zinc-600">Tags <span className="font-normal normal-case text-zinc-400">(optional)</span></div>
        {editing ? <TagPicker testid="builder-tags" value={tags} onChange={mark(setTags)} disabled={busy} requestOpen={cb => requireEdit(cb)} /> : <TagBadges testid="builder-tags-readonly" tags={tags} />}
      </div>

      <div className="mt-6 max-w-4xl" onPaste={editing ? handleComponentPaste : undefined}>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">
            Ingredients <span className="normal-case font-normal text-zinc-400">{level ? `(from ${(SOURCE_LEVELS[level] || []).filter((l) => l !== "L0").join(", ")}) · ` : ""}heatmap: red = highest cost share{editing ? " · paste Name⇥Qty rows to bulk-add" : ""}</span>
          </span>
          {editing && (
            <button data-testid="builder-add-component" type="button" onClick={addComponent}
              className="inline-flex items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 py-1 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 no-print">
              <Plus className="h-3 w-3" strokeWidth={1.5} /> Add ingredient
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-zinc-300 text-left">
          <thead>
            <tr className="bg-zinc-100 text-xs font-semibold uppercase tracking-wider text-zinc-600">
              <th className="border-b border-zinc-300 px-3 py-2">Ingredient</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 w-14">Tier</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-24">Qty</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 w-14">Unit</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">Unit Cost</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">Line Cost</th>
              <th className="border-b border-l border-zinc-300 px-3 py-2 text-right w-28">% of Cost</th>
              {editing && <th className="border-b border-l border-zinc-300 px-2 py-2 w-8"></th>}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && <tr><td data-testid="builder-components-empty" colSpan={8} className="px-3 py-6 text-center text-sm text-zinc-400">No ingredients yet.</td></tr>}
            {lines.map((l) => {
              const pct = preCookCost > 0 ? (l.cost / preCookCost) * 100 : 0;
              return (
                <tr key={l.rowId} style={{ backgroundColor: heatColor(pct, maxPct) }} data-testid={`builder-component-row-${l.idx}`}>
                  <td className="border-b border-zinc-200 px-2 py-1.5">
                    {editing ? (
                      <ComponentSelect testid={`builder-component-select-${l.idx}`} options={options} hideLevels={["L0"]} value={l.ref_id ? l.key : ""}
                        triggerRef={(node) => { if (node) componentTriggers.current.set(l.rowId, node); else componentTriggers.current.delete(l.rowId); }}
                        onChange={(v) => { const [t, rid] = v.split(":"); setComps((p) => p.map((c, i) => (i === l.idx ? { ...c, ref_type: t, ref_id: rid } : c))); setDirty(true); }} />
                    ) : (
                      <span data-testid={`builder-component-name-${l.idx}`} className="px-1 text-[13px] font-medium">{l.opt?.label || "(deleted)"}</span>
                    )}
                  </td>
                  <td data-testid={`builder-component-tier-${l.idx}`} className="border-b border-l border-zinc-200 px-3 py-1.5"><span className="rounded-sm bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">{l.opt?.level || "—"}</span></td>
                  <td className="border-b border-l border-zinc-200 px-3 py-1.5 focus-within:ring-1 focus-within:ring-inset focus-within:ring-blue-600">
                    {editing ? (
                      <input data-testid={`builder-component-qty-${l.idx}`} type="number" step="any" value={l.rawQty}
                        onKeyDown={handleQuantityKeyDown}
                        onChange={(e) => { setComps((p) => p.map((c, i) => (i === l.idx ? { ...c, qty: e.target.value } : c))); setDirty(true); }}
                        className="grid-cell-input text-right font-mono" />
                    ) : <span data-testid={`builder-component-qty-${l.idx}`} className="block text-right font-mono text-[13px]">{l.qty}</span>}
                  </td>
                  <td data-testid={`builder-component-unit-${l.idx}`} className="border-b border-l border-zinc-200 px-3 py-1.5 text-[13px] text-zinc-500">{l.opt?.unit || "—"}</td>
                  <td data-testid={`builder-component-unit-cost-${l.idx}`} className="border-b border-l border-zinc-200 px-3 py-1.5 text-right font-mono text-[13px]">₹{(l.opt?.unit_cost || 0).toFixed(4)}</td>
                  <td data-testid={`builder-component-line-cost-${l.idx}`} className="border-b border-l border-zinc-200 px-3 py-1.5 text-right font-mono text-[13px]">₹{l.cost.toFixed(2)}</td>
                  <td className="border-b border-l border-zinc-200 px-3 py-1.5 text-right font-mono text-[13px] font-medium" data-testid={`builder-component-pct-${l.idx}`}>{pct.toFixed(1)}%</td>
                  {editing && (
                    <td className="border-b border-l border-zinc-200 px-2 py-1.5 text-center">
                      <button data-testid={`builder-remove-component-${l.idx}`} onClick={() => { setComps((p) => p.filter((_, i) => i !== l.idx)); setDirty(true); }}
                        className="text-zinc-400 transition-colors hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <div className="mt-6 grid max-w-4xl grid-cols-2 gap-px border border-zinc-300 bg-zinc-300 sm:grid-cols-5">
        {[
          { label: "Pre-Cook Weight", value: `${preCookWeight.toFixed(1)}`, testid: "builder-precook-weight" },
          { label: "Pre-Cook Cost", value: `₹${preCookCost.toFixed(2)}`, testid: "builder-precook-cost" },
          { label: "Post-Cook Weight", value: `${out} ${outputUnit}`, testid: "builder-postcook-weight" },
          { label: "Yield %", value: `${yieldPct.toFixed(1)}%`, testid: "builder-yield", warn: yieldPct > 0 && yieldPct < 100 },
          { label: `Post-Cook Cost / ${outputUnit}`, value: `₹${epUnitCost.toFixed(4)}`, testid: "builder-ep-cost", highlight: true },
        ].map((s) => (
          <div key={s.label} className="bg-zinc-50 px-3 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-zinc-500">{s.label}</div>
            <div data-testid={s.testid} className={`mt-0.5 font-mono text-sm font-medium ${s.warn ? "text-amber-600" : s.highlight ? "text-blue-700" : "text-zinc-900"}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex max-w-4xl flex-wrap items-end gap-4 border border-zinc-300 bg-zinc-50 p-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-600">Portion Size ({outputUnit})</label>
          <input data-testid="builder-portion-input" disabled={!editing} type="number" step="any" value={portionQty} onChange={(e) => mark(setPortionQty)(e.target.value)} placeholder="e.g., 150"
            className="mt-1 block w-40 rounded-sm border border-zinc-300 px-3 py-2 text-right font-mono text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600 disabled:bg-white disabled:text-zinc-600" />
        </div>
        <div className="pb-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-zinc-500">Cost per Portion</div>
          <div data-testid="builder-portion-cost" className="font-mono text-xl font-semibold text-zinc-900">₹{portionCost.toFixed(2)}</div>
        </div>
      </div>

      {editing && (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-300 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:left-64 md:px-8 no-print">
          <span className={`text-sm ${dirty ? "font-medium text-amber-600" : "text-zinc-400"}`} data-testid="builder-dirty-indicator">
            {dirty ? "● Unsaved changes" : "All changes saved"}
          </span>
          <div className="flex gap-2">
            <button data-testid="builder-footer-back-button" onClick={goBack} className="rounded-sm border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">Back</button>
            <button data-testid="builder-save-button" onClick={() => save()} disabled={busy}
              className="inline-flex items-center gap-2 rounded-sm bg-blue-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50">
              <Save className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Saving…" : "Save recipe"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
